import React, {useEffect, useState} from 'react';
import {classes} from './ItemOptions.st.css';
import {TextButton, TextButtonPriority, Text} from 'wix-ui-tpa/cssVars';
import ChevronDown from 'wix-ui-icons-common/on-stage/ChevronDown';
import ChevronUp from 'wix-ui-icons-common/on-stage/ChevronUp';
import {useExperiments} from '@wix/yoshi-flow-editor';
import {SPECS} from '../../../constants';
import {DetailsGroup} from './DetailsGroup';
import {DetailLine} from './DetailLine';
import {useControllerProps} from '../../../../components/Checkout/Widget/ControllerContext';

export enum ItemDetailsDataHook {
  CollapseExpandButton = 'ItemDetailsDataHook.collapseExpandButton',
  GroupName = 'ItemDetailsDataHook.groupName',
  GroupOptionsList = 'ItemDetailsDataHook.groupOptionsList',
  UnitBasePrice = 'ItemDetailsDataHook.UnitBasePrice',
}

export interface ItemDetailsProps {
  dataHook: string;
  itemId: string;
  details: {
    descriptionLines?: string[];
    groups?: {descriptionLines: string[]; groupName?: string}[];
  };
  unitBasePrice?: string;
  collapseSettings: {
    numberOfOptionsToCollapseAfter: number;
    showMoreTitle: string;
    showLessTitle: string;
  };
}

// eslint-disable-next-line sonarjs/cognitive-complexity
export const ItemDetails = ({itemId, details, unitBasePrice, collapseSettings, dataHook}: ItemDetailsProps) => {
  const {experiments} = useExperiments();
  const {reportClickToOpenCloseMoreDetails, checkout} = useControllerProps().checkoutStore;
  const supportModifiersOnCheckout = experiments.enabled(SPECS.SupportModifiersOnCheckout);
  const {numberOfOptionsToCollapseAfter, showMoreTitle, showLessTitle} = collapseSettings;
  const descriptionLines = details.descriptionLines;
  const groups = details.groups;
  const numberOfDescriptionLines = descriptionLines?.length || 0;

  const [shouldCollapseAndExpand] = useState(
    Boolean(
      numberOfDescriptionLines > numberOfOptionsToCollapseAfter || (supportModifiersOnCheckout && !!groups?.length)
    )
  );
  const [numberOfOptionsToShow, setNumberOfOptionsToShow] = useState(
    shouldCollapseAndExpand ? numberOfOptionsToCollapseAfter : numberOfDescriptionLines
  );
  const [isCollapsed, setIsCollapsed] = useState(shouldCollapseAndExpand);

  const onCollapseExpandClick = () => {
    if (supportModifiersOnCheckout) {
      reportClickToOpenCloseMoreDetails({checkout, lineItemId: itemId, action: isCollapsed ? 'open' : 'close'});
    }
    setIsCollapsed(!isCollapsed);
  };

  useEffect(() => {
    setNumberOfOptionsToShow(isCollapsed ? numberOfOptionsToCollapseAfter : numberOfDescriptionLines);
  }, [isCollapsed, numberOfDescriptionLines, numberOfOptionsToCollapseAfter, details]);

  const renderCollapseExpandButton = () => {
    return (
      <TextButton
        className={classes.collapseButton}
        suffixIcon={isCollapsed ? <ChevronDown /> : <ChevronUp />}
        priority={TextButtonPriority.secondary}
        data-hook={ItemDetailsDataHook.CollapseExpandButton}
        onClick={onCollapseExpandClick}>
        {isCollapsed ? showMoreTitle : showLessTitle}
      </TextButton>
    );
  };

  const shouldShowDetailsList = !!descriptionLines?.length || !isCollapsed;

  return (
    <div className={classes.root}>
      {supportModifiersOnCheckout && unitBasePrice && (
        <Text data-hook={ItemDetailsDataHook.UnitBasePrice} className={classes.baseUnitPrice}>
          {unitBasePrice}
        </Text>
      )}
      {shouldShowDetailsList && (
        <ul className={classes.options} data-hook={dataHook}>
          {descriptionLines?.slice(0, numberOfOptionsToShow).map((descriptionLine) => (
            <li key={`option-${itemId}-${descriptionLine}`}>
              <DetailLine value={descriptionLine} />
            </li>
          ))}
          {supportModifiersOnCheckout &&
            !isCollapsed &&
            groups?.map((group) => (
              <li key={`option-${itemId}-${group.groupName}`} className={classes.group}>
                <DetailsGroup itemId={itemId} group={group} />
              </li>
            ))}
        </ul>
      )}
      {shouldCollapseAndExpand && renderCollapseExpandButton()}
    </div>
  );
};
